// Точка входа клиента.
//
// Порядок импортов важен: токены дизайн-системы подключаются ДО кода приложения,
// чтобы CSS-переменные были на месте к первому рендеру.
//
// Шрифты подключаются ПОСЛЕ токенов и self-host'ом: файлы лежат в репозитории,
// рантайм-CDN запрещён (DESIGN.md, офлайн-инвариант). Кухонный моноблок работает
// без сети и обязан рендерить фирменную типографику — запрос к fonts.googleapis.com
// на первом же обрыве связи превратил бы экран печати в системный шрифт
// с поехавшими метриками.
//
// До 2026-07-30 этого импорта не было, а файлов не существовало вовсе: приложение
// рендерилось системным шрифтом, то есть ровно тем классом, который DESIGN.md
// запрещает по имени за качество кириллицы. Комментарий здесь при этом утверждал
// обратное — «шрифты были на месте к первому рендеру», — и потому пробел не видел
// никто. Согласованность теперь сторожит тест в `tokens/__tests__`.

import '@restomarker/ui-kit/reset.css';
import '@restomarker/ui-kit/tokens.css';
import '@restomarker/ui-kit/fonts.css';

import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { alertKeys, restoreDeviceMarks } from '@/modules/notifications';
import { queryClient, restoreServerAddress } from '@/shared/api';
import { initClientVersion, notifyReady } from '@/shared/appUpdate';
import { restoreToken } from '@/shared/auth';
import { initMonitoring, watchSoftKeyboard } from '@/shared/lib';
import { startClockSync } from '@/shared/offline';
import { loadPrinterBinding } from '@/shared/printing';
import { installPushHandlers, showForegroundPush } from '@/shared/push';
import { restoreDeviceTag, restoreViewMode } from '@/shared/storage';

import { AppProviders, ForegroundPushToast } from '../providers';
import { router } from '../router';

initMonitoring();

// Липкие футеры встают на экранную клавиатуру, а не висят над ней (`softKeyboard.ts`).
watchSoftKeyboard();

// Push (решение владельца 2026-09-26): нажатие на уведомление открывает экран, а
// пришедшее при открытом приложении показывается тостом — Android сам его не покажет.
// Подписка до первого рендера: приложение, открытое нажатием на push, получает это
// нажатие сразу на старте, и позже его уже не поймать.
const openFromPush = (route: string): void => router.history.push(route);
installPushHandlers({
  onOpen: openFromPush,
  onForeground: (title, body) => {
    showForegroundPush({ title, body });
    // Тот же прогон крона записал и ленту точки (T-047): счётчик — сразу, не через минуту.
    void queryClient.invalidateQueries({ queryKey: alertKeys.all });
  },
});

const routerPath = {
  get: () => router.state.location.pathname,
  subscribe: (listener: () => void) => router.subscribe('onResolved', listener),
};

const container = document.getElementById('root');

if (!container) {
  // Ломаемся громко: без #root приложение всё равно не поднимется,
  // а молчаливый белый экран на кухонном моноблоке не диагностируется.
  throw new Error('Элемент #root не найден в index.html');
}

// Токен восстанавливается ДО первого рендера и ровно один раз.
//
// Порядок здесь несущий: чтение из хранилища асинхронное, а guard и httpClient читают
// токен синхронно. Смонтируй роутер раньше — и первый запрос уйдёт без заголовка,
// получит 401, а приложение решит, что сессии нет, и выкинет вошедшего пользователя
// на /login. Воспроизводится только на медленном устройстве, то есть ровно на кухонном
// планшете, и выглядит как «иногда разлогинивает».
// Всё локальное состояние восстанавливается ДО первого рендера: сессия человека,
// привязка устройства и АКТИВНЫЙ СОТРУДНИК. Параллельно, а не последовательно — это
// независимые ключи хранилища, и лишняя задержка на старте моноблока не оправдана.
//
// Активный сотрудник сюда попал не сразу, и его отсутствие стоило дорого: значение
// писалось в хранилище, но при старте не читалось. Киоск после каждой перезагрузки
// возвращал повара к выбору себя — инвариант «0 тапов на продолжение смены» не работал,
// — а списание с кухни уходило БЕЗ автора и отклонялось сервером.
//
// Тест на persistence это не поймал: он проверял, что значение попало в хранилище,
// а не то, что приложение читает его при старте. Проверялась запись, а не цикл.
// Замер дрейфа часов включается ДО первого запроса: иначе первый ответ сервера,
// он же обычно единственный за сеанс на плохой сети, проходит мимо замера.
startClockSync();

// Версия клиента для сервера (T-041) — параллельно со стартом, а не до рендера:
// это диагностика, и ждать её кухонному планшету незачем. Первые запросы уйдут
// без заголовка, сервер запишет версию со следующего.
void initClientVersion();

// Привязка принтера (T-031) — тоже до первого рендера: `resolvePrinterAdapter`
// на экране печати читает её синхронно из копии в памяти.
// Адрес сервера сети восстанавливается ДО первого рендера вместе с сессией: гейт
// роутинга (`rootRoute.beforeLoad`) читает его синхронно, и на медленном устройстве
// незаполненная копия увела бы на экран настройки уже настроенный планшет.
void Promise.all([
  restoreServerAddress(),
  restoreToken(),
  restoreViewMode(),
  loadPrinterBinding(),
  // Тег устройства — до первого рендера: суффикс FIFO-номера строится синхронно
  // в момент печати, а без тега два устройства под одной учёткой офлайн выдают
  // один номер (`deviceTag.ts`).
  restoreDeviceTag(),
  // Отметки ленты устройства (T-047) — до первого рендера: счётчик на колокольчике
  // считается синхронно, и без них первый кадр показал бы «99+».
  restoreDeviceMarks(),
]).finally(() => {
  createRoot(container).render(
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
        <ForegroundPushToast onOpen={openFromPush} path={routerPath} />
      </AppProviders>
    </StrictMode>,
  );

  // Сообщаем апдейтеру, что бандл поднялся: без этого вызова в срок плагин сочтёт
  // текущий веб-слой сломанным и откатится на предыдущий (offline-first.md §2).
  // ПОСЛЕ render, а не до: «поднялся» значит, что дерево смонтировано. На вебе — no-op.
  void notifyReady();

  // `restorePush()` отсюда убран (B-039): он регистрировал адрес на того, кто окажется
  // вошедшим, не спрашивая его выбора. Подключение push теперь — `usePushSync`
  // в `AppLayout`, по выбору человека с сервера, при каждом запуске и входе.
});
